import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import Button from "@/design-system/button";
import { languageSelector } from "@/src/contexts";
import { DiscountReviewResources } from "@/src/discount/DiscountReview.Resources";
import { DiscountReviewResourcesEn } from "@/src/discount/DiscountReview.Resources.En";
import { AnimationApi, AnimationType } from "@Components/AnimationApi";
import { defaultAttributes } from "@Components/Component";
import useAjax from "@Components/Hooks/useAjax";
import { Loading } from "@Components/Loading";
import { urlFormat } from "@Components/Modules/Helpers";
import { Panel } from "@Components/Panel";
import { Discount } from "@Routes/DiscountLinks";
import * as stylex from "@stylexjs/stylex";

import { colors, genericStyles } from "../../styles/tokens.stylex";
import { Unauthorized } from "../error-handling/Unauthorized/Unauthorized";
import type { IDiscountReviewOpinion } from "./DiscountReviewOpinion";
import { DiscountReviewOpinion } from "./DiscountReviewOpinion";

import * as css from "./DiscountReview.module.css";

interface IComment {
    author: string;
    moment: string;
    commentText: string;
    starCount: number;
    replies: IComment[];
}

interface ICommentListResult {
    page?: number;
    comments: IComment[];
    hasMoreComments: boolean;
}

export interface IDiscountReview {
    id: string;
    brandName: string;
    reviewOpinionModal: IDiscountReviewOpinion;
    isCommentDisabled: boolean;
    isAuthenticated: boolean;
    initialData: ICommentListResult;
}

interface IDiscountReviewState {
    showReviewModal: boolean;
    hasMoreComments: boolean;
    comments: IComment[];
    disabled: boolean;
    renderUnauthorized: boolean;
    page?: number;
}

const DiscountReview: FC<IDiscountReview> = (props) => {
    const [state, setState] = useState<IDiscountReviewState>({
        showReviewModal: false,
        page: props.initialData?.page || 0,
        hasMoreComments: (props && props.initialData?.hasMoreComments) || false,
        comments: props.initialData?.comments || [],
        disabled: props.isCommentDisabled,
        renderUnauthorized: false,
    });

    const resources = languageSelector(DiscountReviewResources, DiscountReviewResourcesEn);

    const allComments = useRef(false);

    const ajaxSuccess = (result: ICommentListResult) => {
        let items: IComment[] = state.comments;

        if (allComments.current) {
            Array.prototype.push.apply(items, result.comments);
        } else {
            items = result.comments;
        }

        setState((currState) => ({
            ...currState,
            comments: items,
            page: result.page || null,
            hasMoreComments: result.hasMoreComments,
        }));
    };

    const { request, isLoading } = useAjax<ICommentListResult>({
        ajaxAction: urlFormat(Discount, { action: "GetComments", id: props.id, page: "{page}" }, true),
        initialData: props.initialData,
        success: ajaxSuccess,
    });

    const onToggleReviewModal = (open: boolean) => {
        if (state.disabled === false) {
            setState((currState) => ({ ...currState, showReviewModal: open }));
        }
    };

    const getComments = () => {
        let { page } = state;
        if (!allComments) {
            page = 0;
        }

        request({ page });
    };

    useEffect(getComments, []);

    const commentAdded = () => {
        setState((cState) => ({ ...cState, disabled: true }));
        allComments.current = false;
        getComments();
    };

    const addOpinion = () => {
        if (props.isAuthenticated) {
            onToggleReviewModal(true);
        } else {
            setState((cState) => ({ ...cState, renderUnauthorized: true }));
        }
    };

    const closeUnauthorized = () => {
        setState((cState) => ({ ...cState, renderUnauthorized: false }));
    };

    const onModalClose = () => onToggleReviewModal(false);

    const renderModal = () =>
        state.showReviewModal && (
            <DiscountReviewOpinion
                {...props.reviewOpinionModal}
                id={props.id}
                onClose={onModalClose}
                onCommentAdded={commentAdded}
            />
        );

    const renderSingleComment = (item: IComment) => (
        <div className={css.comment}>
            <div className={css.author}>{item.author}</div>
            <div className={css.moment}>
                {resources.momentText}
                <span className={css.momentDate}>{item.moment}</span>
            </div>
            <div className={css.commentText}>{item.commentText}</div>
        </div>
    );

    const renderComments = () => {
        const renderReplies = (item: IComment) => {
            let replies = null;
            if (item.replies && item.replies.length > 0) {
                replies = (
                    <div className={css.commentReplies}>
                        {item.replies.map((reply, j) => (
                            <div key={j}>{renderSingleComment(reply)}</div>
                        ))}
                    </div>
                );
            }
            return replies;
        };

        const comments = state.comments.map((comment, i) => (
            <div key={i}>
                {renderSingleComment(comment)}
                {renderReplies(comment)}
            </div>
        ));

        const renderViewMore = () => {
            if (state.hasMoreComments === true) {
                const showMoreComments = () => {
                    allComments.current = true;
                    getComments();
                };

                return (
                    <div className={css.viewMore}>
                        <Button
                            as="button"
                            size="M"
                            variant="transparent"
                            styleX={styles.blueColorBtn}
                            onClick={showMoreComments}
                        >
                            {resources.viewMoreText}
                        </Button>
                    </div>
                );
            }
            return null;
        };

        const renderCommentButton = () =>
            !state.disabled && (
                <div className={css.reviewButton}>
                    {!props.isAuthenticated && state.renderUnauthorized && (
                        <Unauthorized onClose={closeUnauthorized} />
                    )}
                    <Button as="button" size="M" variant="secondary" onClick={addOpinion}>
                        {state.comments.length === 0 ? resources.remark : resources.remarkComment}
                    </Button>
                </div>
            );

        return (
            <Panel title={resources.panelTitle.replace("{0}", props.brandName)} titleElement="h2">
                {renderCommentButton()}
                <AnimationApi animationType={AnimationType.Fade} duration={500}>
                    {comments}
                </AnimationApi>
                {renderViewMore()}
            </Panel>
        );
    };

    return (
        <div
            {...defaultAttributes(props, css.discountReviewComponent, stylex.props(styles.border).className)}
        >
            {renderModal()}
            {renderComments()}
            {isLoading && <Loading isModal />}
        </div>
    );
};

const styles = stylex.create({
    border: {
        borderRadius: genericStyles.borderRadiusS1,
    },
    blueColorBtn: {
        color: colors.fg_Accent,
    },
});

export default DiscountReview;
